import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useMapContext } from '../../context/MapContext';
import { useProjectContext } from '../../context/ProjectContext';
import {
  createMarkerElement,
  defaultLocation,
  geocodeAddress,
} from './MapHelpers';
import { TabsType } from '../../enums';
import useUpdateProjectName from '../../api/mutations/useUpdateProjectName';
import useProjects from '../../api/queries/useProjects';
import strings from '../../language/Swedish';

interface Props {
  map: google.maps.Map | null;
  isLoaded: boolean;
  isDrawingComplete: boolean;
  isDrawModeOn: boolean;
  isBlockingCenterChange: boolean;
  autocomplete: google.maps.places.Autocomplete | null;
}
const useMapInteractions = ({
  map,
  isLoaded,
  autocomplete,
  isDrawingComplete,
  isDrawModeOn,
  isBlockingCenterChange,
}: Props) => {
  const [center, setCenter] = useState(defaultLocation);
  const markerRefs = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

  const {
    soldMarkerObjects,
    adsMarkerObjects,
    currentCoordinates,
    setCoordinates,
    setIsStreetViewOn,
  } = useMapContext();

  const {
    selectedTab,
    project: { adsTableIds, soldTableIds, id, name },
    setProject,
    toggleSelectedTableIds,
  } = useProjectContext();

  const { refetch: refetchProjects } = useProjects();

  const { mutate: updateProjectNameMutation } = useUpdateProjectName({
    onSuccess: (data) => {
      refetchProjects();

      setProject((prevState) => ({
        ...prevState,
        name: data.name,
      }));
    },
  });

  const markerObjects =
    selectedTab === TabsType.ads ? adsMarkerObjects : soldMarkerObjects;

  const clearMarkers = useCallback(() => {
    markerRefs.current.forEach((marker) => (marker.map = null));
    markerRefs.current = [];
  }, [markerRefs]);

  useEffect(() => {
    if (
      currentCoordinates.lat !== null &&
      currentCoordinates.lng !== null &&
      currentCoordinates.lat !== center.lat &&
      currentCoordinates.lng !== center.lng
    ) {
      setCenter({ lat: currentCoordinates.lat, lng: currentCoordinates.lng });
    }
  }, [currentCoordinates, setCenter, center.lat, center.lng]);

  useEffect(() => {
    if (!markerObjects || markerObjects.length === 0) {
      clearMarkers();
    } else if (map && isLoaded && markerRefs) {
      clearMarkers();

      if (selectedTab === TabsType.ads || selectedTab === TabsType.sold) {
        const collisionBehavior = google.maps.CollisionBehavior.REQUIRED;
        const bounds = new google.maps.LatLngBounds();
        markerObjects.forEach(({ id, index, location }) => {
          if (location.lng && location.lat && id) {
            const isSelected =
              (selectedTab as TabsType) === TabsType.ads
                ? adsTableIds.selected?.has(id.toString())
                : soldTableIds.selected?.has(id.toString());

            const markerElement = createMarkerElement(index, isSelected);

            const marker = new google.maps.marker.AdvancedMarkerElement({
              map,
              position: { lat: location.lat, lng: location.lng },
              content: markerElement,
              collisionBehavior,
            });

            marker.addEventListener('gmp-click', () => {
              toggleSelectedTableIds(selectedTab as TabsType, id);
            });
            const ltLng = new google.maps.LatLng(location.lat, location.lng);
            bounds.extend(ltLng);
            markerRefs.current.push(marker);
          }
        });
         bounds.extend(center);
        // Prevent zooming
        // if (!isMarkerClicked) {
        //   fitBoundsAroundCenter(center, bounds, map, isMapClicked);
        // }
      }
    }
  }, [
    isLoaded,
    map,
    markerObjects,
    soldTableIds,
    adsTableIds,
    selectedTab,
    toggleSelectedTableIds,
  ]);

  useEffect(() => {
    if (map) {
      const panoListener = map
        .getStreetView()
        .addListener('visible_changed', () => {
          const visible = map.getStreetView().getVisible();
          setIsStreetViewOn(visible);
        });

      return () => {
        if (panoListener) google.maps.event.removeListener(panoListener);
      };
    }
  }, [map, setIsStreetViewOn]);

  const onMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      // Only prevent map clicks when actively drawing a new polygon (not when editing existing)
      if (isDrawModeOn && !isDrawingComplete) return;
      
      // Block center changes during polygon completion
      if (isBlockingCenterChange) return;

      if (e.latLng) {
        const newCoordinates = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };
        setCoordinates(newCoordinates);
      }
    },
    [isDrawModeOn, isDrawingComplete, isBlockingCenterChange, setCoordinates]
  );

  const updateMapWithLocation = useCallback(
    (location: google.maps.LatLng) => {
      const newCenter = {
        lat: location.lat(),
        lng: location.lng(),
      };

      setCenter(newCenter);

      map?.panTo(newCenter);

      setCoordinates({ lat: newCenter.lat, lng: newCenter.lng });
    },
    [map, selectedTab, setCenter, setCoordinates]
  );

  const onPlaceChanged = useCallback(
    async (manualAddress: string | null = null) => {
      if (autocomplete !== null && manualAddress === null) {
        const place = autocomplete.getPlace();
        const location = place.geometry?.location;
        const address = place.formatted_address;

        if (location) {
          updateMapWithLocation(location);
        }
        if (id && name === strings.data.noProjectName && address) {
          updateProjectNameMutation({
            id: id,
            newName: address,
          });
        }
      } else if (manualAddress) {
        try {
          const location = await geocodeAddress(manualAddress);
          updateMapWithLocation(location);

          if (id && name === strings.data.noProjectName) {
            updateProjectNameMutation({
              id: id,
              newName: manualAddress,
            });
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    },
    [autocomplete, id, name, updateProjectNameMutation, updateMapWithLocation]
  );

  return { clearMarkers, onPlaceChanged, onMapClick, center };
};

export default useMapInteractions;
